<template>
  <main class="homiii-sync">
    <header class="homiii-sync__header">
      <div>
        <p class="homiii-sync__eyebrow">Homiii</p>
        <h1>Preview de sincronizacion</h1>
      </div>

      <button
        class="homiii-sync__button"
        type="button"
        :disabled="loading"
        @click="runPreview"
      >
        {{ loading ? 'Generando...' : 'Generar preview' }}
      </button>
      <button
        class="homiii-sync__button homiii-sync__button--secondary"
        type="button"
        :disabled="loading || applying || items.length === 0"
        @click="runApply"
      >
        {{ applying ? 'Aplicando...' : 'Aplicar cambios' }}
      </button>
    </header>

    <section class="homiii-sync__controls">
      <label>
        Limit
        <input v-model.number="limit" min="1" max="50" type="number">
      </label>
      <label>
        Offset
        <input v-model.number="offset" min="0" type="number">
      </label>
      <button
        v-if="summary && summary.next_offset !== null"
        class="homiii-sync__button homiii-sync__button--secondary"
        type="button"
        :disabled="loading"
        @click="runNext"
      >
        Siguiente tanda
      </button>
    </section>

    <section v-if="error" class="homiii-sync__alert homiii-sync__alert--error">
      {{ error }}
    </section>

    <section v-if="summary" class="homiii-sync__summary">
      <div>
        <span>Total</span>
        <strong>{{ summary.accommodations_total_for_user }}</strong>
      </div>
      <div>
        <span>Tanda</span>
        <strong>{{ summary.accommodations_total }}</strong>
      </div>
      <div>
        <span>Procesados</span>
        <strong>{{ summary.processed }}</strong>
      </div>
      <div>
        <span>No encontrados</span>
        <strong>{{ summary.not_found }}</strong>
      </div>
      <div>
        <span>Errores</span>
        <strong>{{ summary.errors }}</strong>
      </div>
    </section>

    <section v-if="logs.length" class="homiii-sync__panel">
      <h2>Logs</h2>
      <pre>{{ logsText }}</pre>
    </section>

    <section v-if="notFound.length" class="homiii-sync__panel">
      <h2>No encontrados</h2>
      <ul class="homiii-sync__list">
        <li v-for="item in notFound" :key="`not-found-${item.idalojamiento}`">
          {{ item.message }} - alojamiento local {{ item.idalojamiento }}
        </li>
      </ul>
    </section>

    <section v-if="items.length" class="homiii-sync__results">
      <article
        v-for="item in items"
        :key="item.idalojamiento"
        class="homiii-sync__item"
      >
        <header>
          <div>
            <span>ALOJAMIENTO ID: {{ item.idalojamiento }}</span>
            <strong>idcodigo {{ item.idcodigo }}</strong>
          </div>
          <small>{{ item.images_detected_count }} imagenes detectadas</small>
        </header>

        <table v-if="item.changes.length" class="homiii-sync__table">
          <thead>
            <tr>
              <th>Columna</th>
              <th>Antes</th>
              <th>Nuevo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="change in item.changes" :key="`${item.idalojamiento}-${change.column}`">
              <td>{{ change.column }}</td>
              <td>{{ formatValue(change.old) }}</td>
              <td>{{ formatValue(change.new) }}</td>
            </tr>
          </tbody>
        </table>

        <p v-else class="homiii-sync__empty">Sin cambios de columnas.</p>

        <details v-if="item.images_detected.length" class="homiii-sync__images">
          <summary>Ver imagenes detectadas</summary>
          <ul>
            <li v-for="image in item.images_detected" :key="image.source_url">
              <a :href="image.source_url" target="_blank" rel="noopener noreferrer">
                {{ image.source_url }}
              </a>
            </li>
          </ul>
        </details>
      </article>
    </section>
  </main>
</template>

<script>
const API_BASE_URL = (process.env.VUE_APP_API_URL || 'http://hyf-api.local:8429/api/v1/').replace(/\/+$/, '');

export default {
  name: 'HomiiiSyncPreview',
  data() {
    return {
      loading: false,
        applying: false,
      error: '',
      summary: null,
      logs: [],
      notFound: [],
      items: [],
      limit: 10,
      offset: 0
    };
  },
  computed: {
    logsText() {
      return this.logs.join('\n');
    }
  },
  methods: {
    async runPreview() {
      this.loading = true;
      this.error = '';
      this.summary = null;
      this.logs = [];
      this.notFound = [];
      this.items = [];

      try {
        const response = await fetch(`${API_BASE_URL}/homiii/sync`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            limit: this.limit,
            offset: this.offset
          })
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload.message || 'No se pudo generar el preview.');
        }

        this.summary = payload.summary || null;
        this.logs = payload.logs || [];
        this.notFound = payload.not_found || [];
        this.items = payload.items || [];
      } catch (error) {
        this.error = error.message || 'Error generando preview Homiii.';
      } finally {
        this.loading = false;
      }
    },
    async runApply() {
      if (!window.confirm('¿Aplicar los cambios detectados a los alojamientos seleccionados?')) {
        return;
      }

      this.applying = true;
      this.error = '';

      try {
        const response = await fetch(`${API_BASE_URL}/homiii/apply`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            limit: this.limit,
            offset: this.offset
          })
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload.message || 'No se pudo aplicar los cambios.');
        }

        this.summary = payload.summary || null;
        this.logs = payload.logs || [];
        this.notFound = payload.not_found || [];
        this.items = payload.items || [];
      } catch (error) {
        this.error = error.message || 'Error aplicando cambios Homiii.';
      } finally {
        this.applying = false;
      }
    },
    runNext() {
      if (!this.summary || this.summary.next_offset === null) {
        return;
      }

      this.offset = this.summary.next_offset;
      this.runPreview();
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return 'null';
      }

      if (typeof value === 'object') {
        return JSON.stringify(value);
      }

      return String(value);
    }
  }
};
</script>

<style scoped>
.homiii-sync {
  width: min(1180px, calc(100% - 32px));
  margin: 32px auto;
  color: #20242a;
}

.homiii-sync__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.homiii-sync__eyebrow {
  margin: 0 0 4px;
  color: #6d7480;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
}

.homiii-sync h1 {
  margin: 0;
  font-size: 28px;
  line-height: 1.2;
}

.homiii-sync__button {
  min-height: 42px;
  padding: 0 18px;
  border: 0;
  border-radius: 6px;
  background: #1f6feb;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.homiii-sync__button:disabled {
  background: #8daee8;
  cursor: wait;
}

.homiii-sync__button--secondary {
  background: #40566f;
}

.homiii-sync__controls {
  display: flex;
  align-items: end;
  gap: 12px;
  margin-bottom: 18px;
}

.homiii-sync__controls label {
  display: grid;
  gap: 6px;
  color: #65707d;
  font-size: 13px;
  font-weight: 700;
}

.homiii-sync__controls input {
  width: 110px;
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid #cfd7e3;
  border-radius: 6px;
}

.homiii-sync__summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.homiii-sync__summary div,
.homiii-sync__panel,
.homiii-sync__item {
  border: 1px solid #d8dee8;
  border-radius: 8px;
  background: #fff;
}

.homiii-sync__summary div {
  padding: 14px;
}

.homiii-sync__summary span {
  display: block;
  color: #65707d;
  font-size: 13px;
}

.homiii-sync__summary strong {
  display: block;
  margin-top: 4px;
  font-size: 24px;
}

.homiii-sync__alert {
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 6px;
}

.homiii-sync__alert--error {
  background: #fff0f0;
  color: #a62929;
}

.homiii-sync__panel {
  margin-bottom: 18px;
  padding: 16px;
}

.homiii-sync__panel h2 {
  margin: 0 0 12px;
  font-size: 18px;
}

.homiii-sync__panel pre {
  max-height: 260px;
  margin: 0;
  overflow: auto;
  white-space: pre-wrap;
}

.homiii-sync__list {
  margin: 0;
  padding-left: 18px;
}

.homiii-sync__results {
  display: grid;
  gap: 16px;
}

.homiii-sync__item {
  overflow: hidden;
}

.homiii-sync__item > header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: #f6f8fb;
}

.homiii-sync__item header span,
.homiii-sync__item header strong {
  display: block;
}

.homiii-sync__item header small {
  color: #586575;
}

.homiii-sync__table {
  width: 100%;
  border-collapse: collapse;
}

.homiii-sync__table th,
.homiii-sync__table td {
  padding: 12px 16px;
  border-top: 1px solid #e7ebf1;
  text-align: left;
  vertical-align: top;
  word-break: break-word;
}

.homiii-sync__table th {
  color: #65707d;
  font-size: 13px;
}

.homiii-sync__empty {
  margin: 0;
  padding: 16px;
  color: #65707d;
}

.homiii-sync__images {
  padding: 12px 16px 16px;
  border-top: 1px solid #e7ebf1;
}

.homiii-sync__images summary {
  cursor: pointer;
  font-weight: 700;
}

.homiii-sync__images ul {
  margin: 10px 0 0;
  padding-left: 18px;
}

.homiii-sync__images a {
  color: #1f6feb;
  word-break: break-all;
}

@media (max-width: 760px) {
  .homiii-sync__header,
  .homiii-sync__item > header {
    align-items: flex-start;
    flex-direction: column;
  }

  .homiii-sync__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
